import { useState } from 'react'
import { Plus, RefreshCw, Mail, Users, CheckCircle, XCircle } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SearchInput,
  PageHeader,
  toast,
} from '@atrvd/saas-ui'
import {
  useInvitationsPage,
  useResendInvitation,
  type InvitationStatus,
  type OrganizationInvitation,
} from '@/hooks/useOrganizationInvitations'
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useTablePagination } from '@/hooks/useTablePagination'
import { OrganizationInviteForm } from '@/components/admin/OrganizationInviteForm'
import { InvitationsTable } from '@/components/admin/InvitationsTable'
import { SectionHeading } from '@/components/common/SectionHeading'
import { tableQueryState } from '@/components/common/queryErrorDisplay'

const NO_INVITATIONS: OrganizationInvitation[] = []

/**
 * Admin page for managing tenant organization owner invitations
 * Displays invitation statistics, filter controls, and invitation table.
 *
 * The table pages, filters and searches on the server; the stat cards show the
 * server's tallies of every organization invitation, regardless of the status
 * filter and search.
 *
 * Loading and errors stay inside the table section: a failed page (a search the
 * server rejects, a network blip on page 2) keeps the filters, the search box
 * and the footer, so the user can change what caused it or step back.
 */
export function InvitationsPage() {
  const [statusFilter, setStatusFilter] = useState<InvitationStatus | undefined>(undefined)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [invitationSearch, setInvitationSearch] = useState('')
  const [resendingId, setResendingId] = useState<string | null>(null)
  // The box stays bound to `invitationSearch`; the server sees the settled term.
  const search = useDebouncedValue(invitationSearch.trim(), SEARCH_DEBOUNCE_MS)

  // A status or search change goes back to page 1. The total comes back with
  // the page, so the footer (and the clamp when rows leave the last page) goes
  // through `forTotal` after the page query.
  const pagination = useTablePagination({
    totalItems: undefined,
    resetKey: JSON.stringify([statusFilter ?? 'all', search]),
  })
  const invitationsQuery = useInvitationsPage({
    inviteType: 'organization',
    status: statusFilter,
    search,
    page: pagination.page,
    pageSize: pagination.pageSize,
  })
  const { data, refetch } = invitationsQuery
  const { dataTablePagination } = pagination.forTotal({
    total: data?.total,
    isFetching: invitationsQuery.isFetching,
  })
  const resendInvitation = useResendInvitation()
  const stats = data?.counts
  const invitations = data?.invitations ?? NO_INVITATIONS

  /**
   * Handle resending an invitation.
   * Captures the mutation envelope and surfaces a toast when the invitation
   * record was updated but the email delivery failed (emailSent === false).
   * @param id - Invitation ID to resend
   */
  const handleResend = async (id: string) => {
    setResendingId(id)
    try {
      const result = await resendInvitation.mutateAsync(id)
      // Narrow-cast to read the email-delivery envelope without modifying
      // the shared OrganizationInvitation interface.
      if ((result as { emailSent?: boolean } | null)?.emailSent === false) {
        toast.error('Resent invitation saved, but the email could not be sent. Try again.')
      }
    } catch {
      // Error is handled by mutation
    } finally {
      setResendingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenant Invitations"
        description="Manage organization owner invitations"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={() => setIsFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create
            </Button>
          </div>
        }
      />

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invitations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total ?? '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
            <Mail className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.open ?? '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.accepted ?? '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.expired ?? '—'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Status Filter Buttons */}
      <div className="flex gap-2">
        <Button
          variant={statusFilter === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter(undefined)}
        >
          All
        </Button>
        <Button
          variant={statusFilter === 'open' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('open')}
        >
          Open
        </Button>
        <Button
          variant={statusFilter === 'accepted' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('accepted')}
        >
          Accepted
        </Button>
        <Button
          variant={statusFilter === 'expired' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('expired')}
        >
          Expired
        </Button>
      </div>

      {/* A section, not a Card: DataTable draws its own card frame. */}
      <section className="space-y-4">
        <SectionHeading icon={Mail}>All Invitations</SectionHeading>
        <SearchInput
          placeholder="Search by organization name or email..."
          value={invitationSearch}
          onChange={setInvitationSearch}
          clearable
          className="max-w-sm"
        />
        <InvitationsTable
          invitations={invitations}
          pagination={dataTablePagination}
          {...tableQueryState(invitationsQuery, invitations.length)}
          onResend={handleResend}
          resendingId={resendingId}
        />
      </section>

      {/* Organization Invitation Form Modal */}
      <OrganizationInviteForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSuccess={() => {
          refetch()
        }}
      />
    </div>
  )
}